import React from 'react';
import { PokemonCard } from '../../types/pokemon';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { useTranslation } from '../../hooks/useTranslation';
import { X } from 'lucide-react';

interface SelectedCardsGridRowProps {
  cards: PokemonCard[];
  onRemoveCard?: (card: PokemonCard) => void;
  cardQuantities?: Record<number, number>;
  title?: string;
  className?: string;
}

export const SelectedCardsGridRow: React.FC<SelectedCardsGridRowProps> = ({
  cards,
  onRemoveCard,
  cardQuantities = {},
  title = 'Cartes sélectionnées',
  className = '',
}) => {
  const { t, lang } = useTranslation();
  
  if (cards.length === 0) {
    return null;
  }

  const getCardName = (card: PokemonCard): string => {
    if (lang === 'en') {
      return card.card_en || card.card_fr || '';
    }
    return card.card_fr || card.card_en || '';
  };
  
  const getImageUrl = (card: PokemonCard): string => {
    const basePath = `${SUPABASE_IMAGE_BASE_URL}`;
    const imagePath = card.image || '';
    
    if (lang === 'en') {
      return `${basePath}en/${imagePath}`;
    }
    
    return `${basePath}${imagePath}`;
  };

  return (
    <div className={`bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100 p-3 sm:p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
          <span>{title}</span>
          <span className="bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full text-xs">
            {cards.length}
          </span>
        </h3>
      </div>
      
      <div className="flex flex-wrap gap-2 sm:gap-3">
        {cards.map((card) => (
          <div 
            key={card.id} 
            className="relative group transition-transform hover:scale-105"
            title={getCardName(card)}
          >
            <div className="w-14 sm:w-16 aspect-[2/3] relative">
              <img
                src={getImageUrl(card)}
                alt={getCardName(card)}
                className="absolute inset-0 w-full h-full object-contain rounded-md shadow-sm group-hover:shadow-md transition-shadow"
              />
              
              {/* Badge de quantité */}
              {cardQuantities[card.id] > 0 && (
                <div className="absolute -top-1 -right-1 bg-purple-100 text-purple-600 text-xs px-1.5 py-0.5 rounded-full font-medium shadow-sm">
                  {cardQuantities[card.id]}x
                </div>
              )}
              
              {/* Overlay et bouton pour retirer la carte */}
              {onRemoveCard && (
                <>
                  <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity rounded-md"></div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveCard(card);
                    }}
                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-red-600"
                    title={t('common.remove')}
                    aria-label={t('common.remove')}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}; 